import { JSX, useCallback, useEffect, useRef, useState } from 'react'

import { get } from 'lodash'
import { ChevronDown, X } from 'lucide-react'
import DatePicker from 'react-datepicker'
import {
  Controller,
  FieldError,
  FieldValues,
  useFormState,
} from 'react-hook-form'

import {
  DISABLED_INPUT_CLASSNAME,
  DisabledReasonTooltip,
  Group,
} from '~/components/component-library'
import { DateInputProps } from '~/components/component-library/DateInput/types'
import { ErrorFooter, visibleError } from '~/components/inputs/ErrorFooter'
import { usePointerDownOutside } from '~/hooks/usePointerDownOutside'
import { cn } from '~/utils/tailwind-utils'

const DATE_PICKER_IGNORED_SELECTORS = [
  '.react-datepicker',
  '.react-datepicker-popper',
  '.react-datepicker__portal',
]

type DateInputContentProps = Pick<
  DateInputProps,
  | 'minDate'
  | 'maxDate'
  | 'includeBorder'
  | 'placeholder'
  | 'disabled'
  | 'disabledReason'
  | 'label'
  | 'description'
  | 'footer'
  | 'tooltip'
  | 'onClear'
  | 'onBlur'
  | 'size'
  | 'optional'
  | 'showTimeSelect'
> & {
  value: Date | null
  onChange: (date: Date | null) => void
  /** Already-gated error message; drives the red border and the footer text. */
  message?: string
}

const DateInputContent = ({
  value,
  onChange,
  onBlur,
  message,
  minDate,
  maxDate,
  includeBorder = true,
  placeholder = 'Select a date',
  disabled = false,
  disabledReason,
  label,
  description,
  footer,
  tooltip,
  onClear,
  size = 'base',
  optional = false,
  showTimeSelect = false,
}: DateInputContentProps): JSX.Element => {
  const [isOpen, setIsOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const openedViaKeyboardRef = useRef(false)
  const handleOutsidePointerDown = useCallback(() => {
    setIsOpen(false)
  }, [])

  useEffect(() => {
    if (isOpen && openedViaKeyboardRef.current) {
      openedViaKeyboardRef.current = false
      const timer = setTimeout(() => {
        const day = document.querySelector(
          '.react-datepicker__day[tabindex="0"]',
        ) as HTMLElement | null
        const target =
          day ??
          (document.querySelector(
            '.react-datepicker__day--today',
          ) as HTMLElement | null)
        // focusVisible ensures the focus ring shows for programmatic focus
        target?.focus({ focusVisible: true } as FocusOptions)
      }, 0)
      return () => clearTimeout(timer)
    }
  }, [isOpen])

  usePointerDownOutside({
    enabled: isOpen,
    containerRef,
    ignoredSelectors: DATE_PICKER_IGNORED_SELECTORS,
    onOutsidePointerDown: handleOutsidePointerDown,
  })

  const isSmall = size === 'small'
  const showClearButton = onClear && value

  return (
    <Group
      label={label}
      secondaryLabel={optional ? 'Optional' : undefined}
      footer={message ? <ErrorFooter message={message} /> : footer}
      description={description}
      tooltip={tooltip}
    >
      <DisabledReasonTooltip
        disabled={disabled}
        disabledReason={disabledReason}
        className="block w-full"
      >
        <div
          ref={containerRef}
          onClick={(e) => {
            if (disabled) return
            // Don't re-open if click originated from within the date picker popover
            if ((e.target as HTMLElement).closest('.react-datepicker')) return
            setIsOpen(true)
            // Focus the input so focus-within ring appears
            containerRef.current?.querySelector('input')?.focus()
          }}
          className={cn(
            'relative flex w-full cursor-pointer items-center',
            includeBorder &&
              cn(
                'rounded-lg outline-none ring-1 ring-inset transition',
                disabled && !message
                  ? cn('bg-white dark:bg-gray-900', DISABLED_INPUT_CLASSNAME)
                  : cn(
                      'bg-white dark:bg-gray-900',
                      !isOpen && 'hover:bg-gray-50 dark:hover:bg-gray-800',
                    ),
                message
                  ? 'ring-red-500 focus-within:ring-2 focus-within:ring-red-500 dark:ring-red-500'
                  : 'ring-gray-200 focus-within:ring-2 focus-within:ring-blue-500 dark:ring-gray-700',
              ),
          )}
        >
          <DatePicker
            open={isOpen}
            preventOpenOnFocus
            onInputClick={() => setIsOpen(true)}
            selected={value}
            onFocus={(e) => {
              // Prevent browser from selecting the input text on focus
              const input = e.target as HTMLInputElement
              input.setSelectionRange(input.value.length, input.value.length)
            }}
            onBlur={onBlur}
            onChange={(date: Date | null) => {
              onChange(date)
              // Keep the popover open so the user can pick a time after the day.
              if (!showTimeSelect) setIsOpen(false)
            }}
            onKeyDown={(e) => {
              if (e.key === 'Tab') {
                setIsOpen(false)
                return
              }
              if (e.key === 'Enter') {
                e.preventDefault()
                if (!isOpen) {
                  openedViaKeyboardRef.current = true
                }
                setIsOpen((prev) => !prev)
                return
              }
              if (e.key === 'Backspace' && onClear && value) {
                e.preventDefault()
                onClear()
                return
              }
              // Prevent all other keyboard input
              e.preventDefault()
            }}
            className={cn(
              'w-full cursor-pointer select-none rounded-lg bg-transparent text-primary transition placeholder:text-placeholder focus:outline-none focus-visible:outline-none',
              'cursor-pointer caret-transparent',
              isSmall
                ? 'py-1.5 pl-3 pr-2 text-sm'
                : 'py-2.5 pl-3.5 pr-3 text-base',
              includeBorder && message && 'text-error',
              disabled && 'cursor-not-allowed',
            )}
            popperPlacement="bottom-start"
            // Fixed positioning escapes overflow clipping (dialog bodies
            // scroll, and an inline absolute popover flipped upward gets its
            // header cut off) while keeping the popover in the dialog's DOM
            // so outside-click and focus handling still see it as inside.
            popperProps={{ strategy: 'fixed' }}
            // Always six week rows: a popover flipped above the input is
            // pinned by its bottom edge, so a height change between months
            // would move the header and nav buttons.
            fixedHeight
            // Match the standard anchored entrance (scale-in from the top-left).
            // Applied to the calendar, not the popper, so it doesn't fight positioning.
            calendarClassName="origin-top-left animate-in fade-in-0 zoom-in-95 slide-in-from-top-2 duration-200"
            placeholderText={placeholder}
            minDate={minDate}
            maxDate={maxDate}
            renderCustomDayName={(day) => day.shortName}
            showTimeSelect={showTimeSelect}
            timeIntervals={15}
            dateFormat={showTimeSelect ? 'MMM d, yyyy h:mm aa' : 'MMM d, yyyy'}
            wrapperClassName="w-full"
            disabled={disabled}
            autoComplete="off"
          />
          <div
            className={cn(
              'flex flex-shrink-0 items-center',
              isSmall ? 'pr-2' : 'pr-3',
              !showClearButton && 'pointer-events-none',
            )}
          >
            {showClearButton ? (
              <button
                type="button"
                tabIndex={-1}
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  onClear()
                }}
              >
                <X className="h-4 w-4 text-primary" />
              </button>
            ) : (
              <ChevronDown
                className={cn(
                  'flex-shrink-0',
                  isSmall ? 'size-4' : 'size-5',
                  message
                    ? 'text-error'
                    : value
                      ? 'text-secondary'
                      : 'text-placeholder',
                )}
              />
            )}
          </div>
        </div>
      </DisabledReasonTooltip>
    </Group>
  )
}

DateInputContent.displayName = 'DateInputContent'

const FormDateInput = <FormType extends FieldValues = FieldValues>({
  control,
  inputName,
  optional = false,
  requiredErrorMessage = 'Required',
  validationFunc = () => true,
  onChange,
  onBlur,
  onClear,
  ...content
}: Omit<
  Extract<DateInputProps<FormType>, { control: object }>,
  'value'
>): JSX.Element => {
  const { errors, isSubmitted } = useFormState({ control, name: inputName })
  const error = get(errors, inputName) as FieldError | undefined

  return (
    <Controller
      control={control}
      name={inputName}
      render={({ field, fieldState }) => {
        const message = visibleError(error, {
          isTouched: fieldState.isTouched,
          isEmpty: !field.value,
          isSubmitted,
        })
        return (
          <DateInputContent
            {...content}
            optional={optional}
            value={field.value ?? null}
            onChange={(date) => {
              // Selecting a day always yields a Date; clearing goes through
              // onClear below, so never write null via this path.
              if (date) {
                field.onChange(date)
                onChange?.(date)
              }
            }}
            onBlur={() => {
              onBlur?.()
              return field.onBlur()
            }}
            onClear={
              onClear
                ? () => {
                    field.onChange(null)
                    onClear()
                  }
                : undefined
            }
            message={message}
          />
        )
      }}
      rules={{
        required: {
          value: !optional,
          message: requiredErrorMessage,
        },
        validate: validationFunc,
      }}
    />
  )
}

export const DateInput = <FormType extends FieldValues = FieldValues>(
  props: DateInputProps<FormType>,
): JSX.Element => {
  // react-hook-form mode (`control` is the discriminant; `inputName` comes with it)
  if (props.control) {
    return <FormDateInput {...props} />
  }

  // Controlled mode (no react-hook-form): the consumer owns value/onChange.
  // Validation errors live in the react-hook-form path only.
  const { value, onChange, ...content } = props
  return <DateInputContent {...content} value={value} onChange={onChange} />
}

DateInput.displayName = 'DateInput'

export type { DateInputProps } from '~/components/component-library/DateInput/types'
